/**
 * useTimetableWeek Hook
 *
 * Fetches a 7-day timetable view combining static sessions,
 * dynamic task instances, and capacity utilisation per day.
 *
 * US-PPL-001 Phase 5: Enhanced Timetable View
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toLocalDateString } from '../utils/dateUtils'

/**
 * Get the Monday of the week containing the given date
 */
export function getMonday(date = new Date()) {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  d.setDate(diff)
  return toLocalDateString(d)
}

/**
 * Hook to fetch timetable data for a specific week
 * @param {string} weekStart - Monday date in YYYY-MM-DD format
 * @returns {Object} { days, loading, error, refetch }
 */
export function useTimetableWeek(weekStart) {
  const [days, setDays] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchWeek = useCallback(async () => {
    if (!weekStart) {
      setDays([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: rpcError } = await supabase.rpc('get_timetable_week', {
        p_week_start: weekStart
      })

      if (rpcError) throw rpcError
      setDays(data || [])
    } catch (err) {
      console.error('[useTimetableWeek] Fetch error:', err)
      setError(err.message || 'Failed to fetch timetable week')
      setDays([])
    } finally {
      setLoading(false)
    }
  }, [weekStart])

  useEffect(() => {
    fetchWeek()
  }, [fetchWeek])

  return { days, loading, error, refetch: fetchWeek }
}
